import React, { useState, useEffect, useCallback } from "react";
import { haProxyJson } from "./haProxy";

/* ══ SMART HOME ENERGY DASHBOARD ══════════════════════════
   Displays power and energy consumption from HA sensors with
   device_class "power" (W/kW, current draw) or "energy"
   (Wh/kWh, cumulative usage).

   Pulls historical data via HA REST:
   GET /api/history/period/{iso_start}?filter_entity_id=...
   Returns state history for the last N hours.              */

const HOURS_OF_HISTORY = 24;

/* ── Helpers ── */
function parseNum(state) {
  const n = parseFloat(state);
  return isNaN(n) ? null : n;
}

function toKw(value, unit) {
  if (value == null) return null;
  if (unit === "W") return value / 1000;
  return value; /* assume kW */
}

function toKwh(value, unit) {
  if (value == null) return null;
  if (unit === "Wh") return value / 1000;
  if (unit === "MWh") return value * 1000;
  return value; /* assume kWh */
}

function classifyEnergyEntity(entity) {
  const attrs = entity.attributes || {};
  const cls = attrs.device_class;
  const name = (entity.friendly_name || entity.entity_id).toLowerCase();
  const id = entity.entity_id.toLowerCase();

  if (cls !== "energy" && cls !== "power") return null;

  /* Heuristic classification by entity name / id */
  if (id.includes("solar") || name.includes("solar") || name.includes("pv")) return "solar";
  if (id.includes("battery") || name.includes("battery")) return "battery";
  if (id.includes("grid") || name.includes("grid") || id.includes("mains")) return "grid";
  if (id.includes("total") || name.includes("total") || name.includes("home")) return "home";
  return "other";
}

function SmartHomeEnergy({ entities, locId, isRTL }) {
  const energyEntities = entities.filter(e => {
    if (e.domain !== "sensor") return false;
    const cls = e.attributes?.device_class;
    return cls === "energy" || cls === "power";
  });

  const [history, setHistory] = useState({}); /* entity_id → array of {time, value} */
  const [loadingHistory, setLoadingHistory] = useState(false);

  /* ── Fetch 24h history for energy sensors via backend proxy ── */
  const fetchHistory = useCallback(async () => {
    if (!locId || energyEntities.length === 0) return;
    setLoadingHistory(true);

    const start = new Date(Date.now() - HOURS_OF_HISTORY * 3600000).toISOString();
    const ids = energyEntities.map(e => e.entity_id).join(",");

    try {
      const data = await haProxyJson({
        locId,
        path: `/api/history/period/${start}?filter_entity_id=${encodeURIComponent(ids)}&minimal_response`,
      });

      /* Response is array-of-arrays, one per entity */
      const byEntity = {};
      for (const arr of data || []) {
        if (!arr.length) continue;
        const eid = arr[0].entity_id;
        byEntity[eid] = arr
          .map(row => ({ time: new Date(row.last_changed || row.last_updated).getTime(), value: parseNum(row.state) }))
          .filter(p => p.value != null);
      }
      setHistory(byEntity);
    } catch { /* ignore */ }
    finally { setLoadingHistory(false); }
  }, [locId, energyEntities.length]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    fetchHistory();
    const t = setInterval(fetchHistory, 60000); /* refresh every 1 minute */
    return () => clearInterval(t);
  }, [fetchHistory]);

  if (!energyEntities.length) return (
    <div className="sh-scenes-empty">
      <svg width="40" height="40" viewBox="0 0 40 40" fill="none" stroke="var(--g300)" strokeWidth="1.3">
        <path d="M22 4L8 24h10L16 36l14-20H20l2-12z"/>
      </svg>
      <p>{isRTL ? "لم يتم العثور على مستشعرات طاقة" : "No energy sensors found."}</p>
      <p className="sh-scenes-hint">
        {isRTL ? "أضف مستشعرات الطاقة في Home Assistant لتظهر هنا" : "Add energy sensors in HA Energy integration to see them here."}
      </p>
    </div>
  );

  /* ── Current power snapshot ── */
  const powerEntities = energyEntities.filter(e => e.attributes?.device_class === "power");
  const energyTotalEntities = energyEntities.filter(e => e.attributes?.device_class === "energy");

  /* Categorized current power */
  const cats = { grid: 0, solar: 0, battery: 0, home: 0, other: 0 };
  const catUnits = {};
  for (const e of powerEntities) {
    const cat = classifyEnergyEntity(e) || "other";
    const v = toKw(parseNum(e.state), e.attributes?.unit_of_measurement);
    if (v == null) continue;
    cats[cat] += v;
    catUnits[cat] = "kW";
  }

  /* Today's cumulative energy */
  const todayCats = { grid: 0, solar: 0, battery: 0, home: 0, other: 0 };
  for (const e of energyTotalEntities) {
    const cat = classifyEnergyEntity(e) || "other";
    const v = toKwh(parseNum(e.state), e.attributes?.unit_of_measurement);
    if (v == null) continue;
    todayCats[cat] += v;
  }

  const maxPower = Math.max(1, ...Object.values(cats).map(Math.abs));

  return (
    <div className="sh-energy">
      {/* Current power snapshot */}
      <div className="sh-energy-cards">
        {cats.solar > 0 && (
          <EnergyCard label={isRTL ? "الطاقة الشمسية" : "Solar"} value={cats.solar} unit="kW" color="#f59e0b" icon="☀" sub={isRTL ? "إنتاج حالي" : "Producing now"} />
        )}
        {cats.grid !== 0 && (
          <EnergyCard label={isRTL ? "الشبكة" : "Grid"} value={cats.grid} unit="kW" color={cats.grid > 0 ? "var(--red,#ef4444)" : "var(--green,#16a34a)"} icon="⚡" sub={isRTL ? (cats.grid > 0 ? "استهلاك" : "تصدير") : (cats.grid > 0 ? "Importing" : "Exporting")} />
        )}
        {cats.battery !== 0 && (
          <EnergyCard label={isRTL ? "البطارية" : "Battery"} value={Math.abs(cats.battery)} unit="kW" color="#3b82f6" icon="🔋" sub={isRTL ? (cats.battery > 0 ? "تفريغ" : "شحن") : (cats.battery > 0 ? "Discharging" : "Charging")} />
        )}
        {cats.home > 0 && (
          <EnergyCard label={isRTL ? "المنزل" : "Home"} value={cats.home} unit="kW" color="var(--gold)" icon="🏠" sub={isRTL ? "استهلاك حالي" : "Current load"} />
        )}
      </div>

      {/* Today's totals */}
      {(todayCats.solar > 0 || Math.abs(todayCats.grid) > 0 || todayCats.home > 0) && (
        <div className="sh-energy-totals">
          <h3 className="sh-section-title">{isRTL ? "إجمالي اليوم" : "Today's Totals"}</h3>
          <div className="sh-energy-total-row">
            {todayCats.solar > 0 && <TotalStat label={isRTL ? "شمسية" : "Solar"} value={todayCats.solar.toFixed(1)} unit="kWh" color="#f59e0b" />}
            {Math.abs(todayCats.grid) > 0 && <TotalStat label={isRTL ? "الشبكة" : "Grid"} value={todayCats.grid.toFixed(1)} unit="kWh" color={todayCats.grid > 0 ? "var(--red,#ef4444)" : "var(--green,#16a34a)"} />}
            {todayCats.home > 0 && <TotalStat label={isRTL ? "استهلاك" : "Consumed"} value={todayCats.home.toFixed(1)} unit="kWh" color="var(--gold)" />}
          </div>
        </div>
      )}

      {/* 24h history per entity */}
      <div className="sh-energy-history">
        <h3 className="sh-section-title">
          {isRTL ? "آخر 24 ساعة" : "Last 24 Hours"}
          {loadingHistory && <span className="sh-energy-loading"> · {isRTL ? "جارٍ التحميل" : "loading"}</span>}
        </h3>
        {powerEntities.map(ent => (
          <HistoryBar key={ent.entity_id} entity={ent} data={history[ent.entity_id]} maxPower={maxPower} isRTL={isRTL} />
        ))}
      </div>
    </div>
  );
}

/* ── Energy stat card ── */
function EnergyCard({ label, value, unit, color, icon, sub }) {
  return (
    <div className="sh-energy-card" style={{ borderColor: color }}>
      <div className="sh-energy-icon">{icon}</div>
      <div className="sh-energy-info">
        <div className="sh-energy-val" style={{ color }}>{value.toFixed(value < 10 ? 2 : 1)} <span className="sh-energy-unit">{unit}</span></div>
        <div className="sh-energy-label">{label}</div>
        {sub && <div className="sh-energy-sub">{sub}</div>}
      </div>
    </div>
  );
}

/* ── Today's total stat ── */
function TotalStat({ label, value, unit, color }) {
  return (
    <div className="sh-energy-total">
      <div className="sh-energy-total-val" style={{ color }}>{value}<span className="sh-energy-total-unit"> {unit}</span></div>
      <div className="sh-energy-total-lbl">{label}</div>
    </div>
  );
}

/* ── 24h history sparkline bar chart ── */
function HistoryBar({ entity, data, maxPower, isRTL }) {
  const name = entity.friendly_name || entity.entity_id;
  const unit = entity.attributes?.unit_of_measurement || "";
  const current = parseNum(entity.state);

  if (!data || !data.length) return (
    <div className="sh-hist-row">
      <div className="sh-hist-label">{name}</div>
      <div className="sh-hist-empty">{isRTL ? "لا توجد بيانات" : "No data"}</div>
      <div className="sh-hist-current">{current != null ? `${current} ${unit}` : "—"}</div>
    </div>
  );

  /* Bucket into 24 hourly averages */
  const buckets = Array(24).fill(null).map(() => ({ sum: 0, count: 0 }));
  const now = Date.now();
  for (const p of data) {
    const hoursAgo = Math.floor((now - p.time) / 3600000);
    if (hoursAgo < 0 || hoursAgo >= 24) continue;
    const idx = 23 - hoursAgo;
    buckets[idx].sum += Math.abs(p.value);
    buckets[idx].count++;
  }
  const values = buckets.map(b => b.count > 0 ? b.sum / b.count : 0);
  const max = Math.max(1, ...values, maxPower || 1);

  return (
    <div className="sh-hist-row">
      <div className="sh-hist-label">{name}</div>
      <div className="sh-hist-bars" dir="ltr">
        {values.map((v, i) => (
          <div
            key={i}
            className="sh-hist-bar"
            style={{ height: `${Math.max(2, (v / max) * 100)}%`, opacity: v > 0 ? 1 : 0.15 }}
            title={`${i - 24}h: ${v.toFixed(2)} ${unit}`}
          />
        ))}
      </div>
      <div className="sh-hist-current">{current != null ? `${current} ${unit}` : "—"}</div>
    </div>
  );
}

export default SmartHomeEnergy;
